import { Component } from 'react'
import PropTypes from 'prop-types'
import config from 'config'
import withWidth, { isWidthDown } from '@material-ui/core/withWidth'
import filter from 'lodash/filter'
import pick from 'lodash/pick'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import { LIVE_CHAT } from 'constants/proposalFieldTypes'
import { getProposalField } from 'utils/proposals'
import { connectGetters } from 'react-redux-getters'
import { getMe } from 'selectors/me'
import { getOrderedFilteredProposals } from 'selectors/proposals'
import { isMailChimpConnected, isStripeConnected } from 'selectors/integrations'

let mountedPreview = null
let widgetScript = null
let widgetInstance = null
let allProposals = null
let widgetOpenedOnce = false
let widgetOptions = {
  welcomeMessageDisabled: true,
  placement: 'right',
}

const wrapMethod = func => (...params) => {
  if (widgetInstance) setTimeout(func(...params))
}

const makeProposals = () => filter(allProposals, (proposal) => {
  const { disabled } = proposal.data || {}
  const { proposalId } = widgetOptions
  return !disabled || proposalId === proposal.id
})

export const updateWidgetOptions = wrapMethod((options) => {
  // Merge derived options
  widgetOptions = {
    ...widgetOptions,
    ...options,
  }
  // Disable live chat selection
  const { proposalId } = widgetOptions
  if (proposalId && find(allProposals, proposal => (
    (proposal.id === proposalId || proposal.uuid === proposalId)
    && !!getProposalField(proposal, LIVE_CHAT)
  ))) {
    delete widgetOptions.proposalId
  }
  // Update proposals
  widgetOptions = {
    ...widgetOptions,
    business: {
      ...widgetOptions.business,
      originalProposals: allProposals,
      proposals: makeProposals(),
    }
  }
  widgetInstance.update(widgetOptions)
})

export const openWidget = wrapMethod(() => {
  widgetOpenedOnce = true
  widgetInstance.openWidget()
})

export const openWidgetOnce = wrapMethod(() => {
  if (!widgetOpenedOnce) openWidget()
})

export const openWelcomeMessage = wrapMethod(() => {
  widgetInstance.openWelcomeMessage()
})

const mapGettersToProps = state => ({
  meGetter: getMe(state),
  proposalsGetter: getOrderedFilteredProposals(state),
  isStripeConnectedGetter: isStripeConnected(state),
  isMailChimpConnectedGetter: isMailChimpConnected(state),
})

export default
@connectGetters(mapGettersToProps)
@withWidth()
class WidgetPreview extends Component {
  static propTypes = {
    meGetter: PropTypes.object.isRequired,
    proposalsGetter: PropTypes.object.isRequired,
    isMailChimpConnectedGetter: PropTypes.object.isRequired,
    isStripeConnectedGetter: PropTypes.object.isRequired,
    width: PropTypes.string.isRequired,
    onInit: PropTypes.func,
  }

  static defaultProps = {
    onInit: () => {}
  }

  componentDidMount() {
    const { width } = this.props
    mountedPreview = this

    if (isWidthDown('md', width)) {
      return
    }

    if (!widgetScript) {
      this.mountScript()
      return
    }

    if (!window.Indemandly) {
      widgetScript.onload = this.initWidget
      return
    }

    this.initWidget()
    widgetScript.onload = () => {}
  }

  componentDidUpdate(prevProps) {
    const {
      meGetter,
      proposalsGetter,
      isStripeConnectedGetter,
      isMailChimpConnectedGetter,
    } = this.props

    const {
      meGetter: prevMeGetter,
      proposalsGetter: prevProposalsGetter,
      isStripeConnectedGetter: prevIsStripeConnectedGetter,
      isMailChimpConnectedGetter: prevIsMailChimpConnectedGetter,
    } = prevProps

    const { business: prevBusiness } = prevMeGetter.data
    const { business } = meGetter.data

    // Do nothing when script is not ready
    if (!window.Indemandly) return

    // Init widget when proposals just loaded
    const proposalsJustLoaded = proposalsGetter.isSucceded && !prevProposalsGetter.isSucceded
    if (proposalsJustLoaded) {
      this.initWidget()
      return
    }

    // Update widget when proposals or business changed
    const businessChanged = business !== prevBusiness
    const proposalsChanged = proposalsGetter.data !== prevProposalsGetter.data
    const stripeChanged = isStripeConnectedGetter.data !== prevIsStripeConnectedGetter.data
    const mailChimpChanged = isMailChimpConnectedGetter.data !== prevIsMailChimpConnectedGetter.data
    const shouldUpdate = businessChanged || proposalsChanged || mailChimpChanged || stripeChanged
    if (!shouldUpdate) return

    this.updateWidget()

    // Open welcome message
    const optionsToCompare = ['welcomeMessage', 'welcomeMessageOnly', 'welcomeMessageDisabled']
    const prevWidgetConfig = pick(prevBusiness.widgetConfig, optionsToCompare)
    const widgetConfig = pick(business.widgetConfig, optionsToCompare)
    const welcomeMessageChanged = !isEqual(prevWidgetConfig, widgetConfig)
    if (welcomeMessageChanged) openWelcomeMessage()
  }

  componentWillUnmount() {
    if (mountedPreview !== this) {
      return
    }
    if (widgetScript) {
      widgetScript.onload = () => {}
    }
    if (widgetInstance) {
      widgetInstance.destroy()
      widgetInstance = null
    }
  }

  makeWidgetOptions = () => {
    const {
      meGetter, proposalsGetter, isMailChimpConnectedGetter, isStripeConnectedGetter
    } = this.props

    const { business } = meGetter.data
    const proposals = proposalsGetter.data
    const canReceivePayments = isStripeConnectedGetter.data
    const canCreateSubscriptions = isMailChimpConnectedGetter.data
    const { domain } = business

    allProposals = proposals

    widgetOptions = {
      ...widgetOptions,
      domain,
      business: {
        ...business,
        canReceivePayments,
        canCreateSubscriptions,
        originalProposals: allProposals,
        proposals: makeProposals()
      }
    }
  }

  shouldOpenWelcomeMessage = (prevProps) => {
    const { meGetter: { data: { business } } } = this.props
    const { meGetter: { data: { business: prevBusiness } } } = prevProps
    const optionsToCompare = ['welcomeMessage', 'welcomeMessageOnly', 'welcomeMessageDisabled']
    const prevWidgetConfig = pick(prevBusiness.widgetConfig, optionsToCompare)
    const widgetConfig = pick(business.widgetConfig, optionsToCompare)
    return !isEqual(prevWidgetConfig, widgetConfig)
  }

  mountScript = () => {
    widgetScript = document.createElement('script')
    widgetScript.src = config.widgetLauncherURL
    widgetScript.onload = this.initWidget
    document.body.appendChild(widgetScript)
  }

  initWidget = () => {
    const { proposalsGetter, onInit } = this.props
    if (!proposalsGetter.isSucceded) return
    this.makeWidgetOptions()
    if (widgetInstance) {
      widgetInstance.update(widgetOptions)
    } else {
      widgetInstance = new window.Indemandly(widgetOptions)
    }
    onInit()
  }

  updateWidget() {
    this.makeWidgetOptions()
    if (widgetInstance) widgetInstance.update(widgetOptions)
  }

  render() {
    return null
  }
}
